import { useContext } from 'react';
import { LangContext, STRINGS } from '../../config/i18n';
import { getTenantStatusCfg } from '../../lib/helpers';

// ── Shared UI ─────────────────────────────────────────────────────
export function Modal({ title, onClose, children, wide }){
  return(
    <div style={{position:"fixed",inset:0,zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.78)",backdropFilter:"blur(5px)"}}>
      <div style={{background:"#181b27",border:"1px solid rgba(200,160,60,0.25)",borderRadius:16,width:`min(${wide?680:520}px,95vw)`,maxHeight:"90vh",overflowY:"auto",padding:28,direction:"rtl"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontSize:18,color:"#c8a03c",fontWeight:800}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#64748b",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type="text", required, multiline, rows=3, min, max }){
  const s={width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:14,outline:"none",boxSizing:"border-box",direction:type==="date"||type==="number"?"ltr":"rtl",textAlign:type==="date"||type==="number"?"left":"right",fontFamily:"'Tajawal',sans-serif",resize:"vertical"};
  return(
    <div style={{marginBottom:13}}>
      <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:5}}>{label}{required&&<span style={{color:"#ef4444"}}> *</span>}</label>
      {multiline
        ? <textarea value={value} onChange={e=>onChange(e.target.value)} rows={rows} style={s}/>
        : <input type={type} value={value} min={min} max={max} onChange={e=>onChange(e.target.value)} style={s}/>}
    </div>
  );
}

export function Badge({ status }){
  const lang = useContext(LangContext);
  const cfg=getTenantStatusCfg(status,lang);
  return(
    <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 11px",borderRadius:20,background:cfg.bg,fontSize:11,color:cfg.color,fontWeight:700,whiteSpace:"nowrap"}}>
      <span style={{width:6,height:6,borderRadius:"50%",background:cfg.dot,flexShrink:0}}/>
      {cfg.label}
    </span>
  );
}

export function StatCard({ label, value, accent, icon }){
  return(
    <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"18px 20px",flex:1,minWidth:120,position:"relative",overflow:"hidden",textAlign:"right"}}>
      <div style={{position:"absolute",top:-8,left:-8,fontSize:46,opacity:0.06}}>{icon}</div>
      <div style={{fontSize:30,fontWeight:800,color:accent,lineHeight:1}}>{value}</div>
      <div style={{fontSize:12,color:"#64748b",marginTop:5}}>{label}</div>
    </div>
  );
}

export function MoneyBox({ label, amount, color, sub }){
  return(
    <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:"10px 14px",textAlign:"right",flex:1,minWidth:130}}>
      <div style={{fontSize:10,color:"#475569",marginBottom:4}}>{label}</div>
      <div style={{fontSize:18,fontWeight:800,color,lineHeight:1}}>{amount.toLocaleString()} <span style={{fontSize:11,fontWeight:400}}>ر</span></div>
      {sub&&<div style={{fontSize:10,color:"#64748b",marginTop:3}}>{sub}</div>}
    </div>
  );
}

export function SplitPreview({ splits, managementFeePercent, partners, calcSplit }){
  const ex = calcSplit(1000, {managementFeePercent}, splits);
  return(
    <div style={{background:"rgba(200,160,60,0.06)",border:"1px solid rgba(200,160,60,0.15)",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
      <div style={{fontSize:11,color:"#94a3b8",marginBottom:8,fontWeight:700}}>مثال: إيراد ١٠٠٠ ريال</div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",fontSize:12}}>
        <span style={{color:"#c8a03c"}}>رسوم الإدارة: {ex.fee} ريال</span>
        <span style={{color:"#64748b"}}>صافي التوزيع: {ex.net} ريال</span>
        {splits.map((s,i)=>{
          const k    = s.type==="me" ? "me" : s.partnerId;
          const name = s.type==="me" ? "FindRE" : partners.find(p=>p.id===s.partnerId)?.name||"شريك";
          return <span key={i} style={{color:s.type==="me"?"#c8a03c":"#a78bfa"}}>{name}: {ex.shares[k]||0} ريال</span>;
        })}
        <span style={{color:"#22c55e",fontWeight:700}}>FindRE إجمالاً: {ex.findreTotal} ريال</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// TAB 5 — Customer Registration
// ══════════════════════════════════════════════════════════════════
const FINDRE_EMAIL = "info@findre.sa";

const NATIONALITIES = [
  "سعودي","مصري","سوداني","سوري","يمني","أردني","لبناني","فلسطيني","عراقي","موريتاني",
  "باكستاني","هندي","بنغلاديشي","فلبيني","إندونيسي","سريلانكي","نيبالي","إثيوبي","كيني","أمريكي","بريطاني","أخرى"
];

// ══════════════════════════════════════════════════════════════════
// Invoices Tab — wrapper with sub-tabs: New Invoice | Archive
// ══════════════════════════════════════════════════════════════════

export function CostPill({ label, value, color, bold }){
  if(!value&&value!==0) return null;
  return(
    <div style={{textAlign:"right",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:8,padding:"5px 10px",minWidth:80}}>
      <div style={{fontSize:9,color:"#475569",marginBottom:2}}>{label}</div>
      <div style={{fontSize:13,fontWeight:bold?800:600,color:value>0?color:"#334155"}}>{value>0?value.toLocaleString():"—"} <span style={{fontSize:10,fontWeight:400}}>ر</span></div>
    </div>
  );
}

export function SummCard({ label, value, accent, icon }){
  return(
    <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"16px 18px",flex:1,minWidth:150,position:"relative",overflow:"hidden",textAlign:"right"}}>
      <div style={{position:"absolute",top:-6,left:-6,fontSize:42,opacity:0.06}}>{icon}</div>
      <div style={{fontSize:22,fontWeight:800,color:accent,lineHeight:1,wordBreak:"break-all"}}>{value}</div>
      <div style={{fontSize:11,color:"#64748b",marginTop:5}}>{label}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// BALANCE SHEET TAB
// ══════════════════════════════════════════════════════════════════
const SEED_CASH = {
  current:   19723.23,
  reserve:   5000,
  operations: 1500,
};

export function Row({k, v, ltr}){
  return (
    <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:13}}>
      <span style={{color:"#79858d"}}>{k}</span>
      <span style={{color:"#202a44",fontWeight:700,direction:ltr?"ltr":"rtl"}}>{v}</span>
    </div>
  );
}

// Build signed PDF HTML for download (customer side)


// ── Action helpers (UI-level) ──────────────────────────────────
export function hexToRgb(hex){ const r=parseInt(hex.slice(0,2),16),g=parseInt(hex.slice(2,4),16),b=parseInt(hex.slice(4,6),16); return`${r},${g},${b}`; }

export function lighten(hex){ return hex; }

export function actBtnStyle(color){ return{padding:"4px 11px",borderRadius:6,background:`rgba(${hexToRgb(color.replace("#",""))},0.13)`,border:`1px solid rgba(${hexToRgb(color.replace("#",""))},0.28)`,color,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"'Tajawal',sans-serif"}; }

export function AlertBar({ color, emoji, title, items }){
  return(
    <div style={{background:`rgba(${hexToRgb(color)},0.07)`,border:`1px solid rgba(${hexToRgb(color)},0.2)`,borderRadius:12,padding:"11px 18px",marginBottom:12,display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
      <span style={{fontSize:13,fontWeight:700,color:`#${lighten(color)}`}}>{emoji} {title}</span>
      {items.map((item,i)=><span key={i} style={{fontSize:12,color:`#${color}`,background:`rgba(${hexToRgb(color)},0.09)`,padding:"3px 11px",borderRadius:6}}>{item}</span>)}
    </div>
  );
}

export function SaveBtn({ onClick, disabled }){
  const lang = useContext(LangContext);
  return(
    <button onClick={onClick} disabled={disabled}
      style={{width:"100%",padding:12,borderRadius:10,background:"linear-gradient(135deg,#c8a03c,#9a6f10)",border:"none",color:"#0d0f1a",fontWeight:800,fontSize:14,cursor:disabled?"not-allowed":"pointer",fontFamily:lang==="ar"?"'Tajawal',sans-serif":"'Inter',sans-serif",opacity:disabled?0.4:1}}>
      {STRINGS[lang].save}
    </button>
  );
}
